import React, { useCallback, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { useQuery } from "@apollo/client";
import { GET_CLIENT_AND_INVITATION_WORKERS } from "../../graphql/client";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import ListGroup from "../../components/ListGroup";
import { Invitation, InvitationMode, WorkerInfo } from "../../__generated__/graphql";
import { StackScreenProps } from "@react-navigation/stack";
import { IconName } from "../../components/Icon";
import { useTheme } from "../../hooks/use-theme";
import { StyleSheet } from "react-native";
import Button from "../../components/Button";
import EmptyScreen from "../../components/EmptyScreen";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import NoTalentsImage from "../../assets/images/no-talents-image.svg";
import { useTracking } from "../../hooks/tracking";
import ScreenCard from "../ScreenCard";
import { useDynamicHeaderButton } from "../../hooks/use-dynamic-header-button";
import { useRelevantMissionCounter } from "../../hooks/missions";
import Loading from "../../components/Loading";

type RootParams = StackScreenProps<RootStackParamList>;
type ScreenParams = StackScreenProps<ClientWorkersTabParamList>;

const ClientWorkersScreen: React.FC<ScreenParams> = ({ navigation }) => {
    const i18n = useI18n();
    const styles = useStyles();
    const { colors } = useTheme();
    const { loading, error, data, refetch } = useQuery(GET_CLIENT_AND_INVITATION_WORKERS, {
        fetchPolicy: "cache-and-network",
    });
    const { trackEvent } = useTracking();
    const workers = useMemo(() => data?.getClientWorkers || [], [data?.getClientWorkers]);
    const invitations = useMemo(
        () =>
            data?.allInvitations.elements.filter(
                (invitation) => invitation.mode === InvitationMode.Sender,
            ) || [],
        [data?.allInvitations.elements],
    );
    const getMissionCount = useRelevantMissionCounter();
    const items = useMemo(() => {
        const unionArray: (WorkerInfo | Invitation)[] = [...workers, ...invitations];
        const navigateToItem = (item: WorkerInfo | Invitation) => {
            if ("mode" in item) {
                // Invitation
                navigation.push("ClientInvitationDetails", { invitationId: item.id });
                return;
            }
            navigation.push("WorkerDetails", { workerId: item.id });
        };
        const nonSortedItems = unionArray.map((worker) => {
            const isInvitation = "mode" in worker;
            let workerName = worker.phoneNumber || "";
            if (isInvitation && worker.firstName && worker.lastName) {
                workerName = `${worker.firstName} ${worker.lastName}`;
            }
            if (!isInvitation) {
                workerName = (worker as WorkerInfo).name;
            }
            return {
                id: "0",
                value: worker.id,
                title: workerName,
                icon: isInvitation ? ("talent" as IconName) : undefined,
                iconColor: isInvitation ? colors.onSurfaceDisabled : undefined,
                avatarPath: isInvitation ? undefined : (worker as WorkerInfo).avatarUrl,
                useBigAvatarOrIcon: true,
                description: isInvitation
                    ? undefined
                    : i18n.t("client_worker_cell.mission_count", {
                          count: getMissionCount((worker as WorkerInfo).missionCount),
                      }),
                labels: [
                    {
                        text: isInvitation ? i18n.t("client_worker_cell.invitation") : undefined,
                        mode: "warning" as const,
                    },
                ],
                onPress: () => {
                    navigateToItem(worker);
                },
            };
        });
        return nonSortedItems.sort((a, b) => a.title.localeCompare(b.title));
    }, [workers, invitations, navigation, colors, i18n, getMissionCount]);

    const goToSendInvitation = useCallback(() => {
        void trackEvent("click_create_invitation");
        (navigation as RootParams["navigation"]).navigate("SendInvitation");
    }, [navigation, trackEvent]);

    const dynamicButtonProps = useDynamicHeaderButton(
        items.length > 0 && {
            title: i18n.t("screens.client_profile.tabs.employees.invite_talent"),
            shortTitle: i18n.t("screens.client_profile.tabs.employees.employee"),
            onClick: goToSendInvitation,
        },
    );

    const isInitialLoading = loading && !data;

    return (
        <ScreenCard
            refresh={refetch}
            isContentContainerFlex={!error && (items.length === 0 || isInitialLoading)}
            {...(!error ? dynamicButtonProps : {})}
        >
            <LoadingBoundary
                error={
                    error
                        ? i18n.t("screens.client_profile.tabs.employees.cannot_load_workers")
                        : undefined
                }
                loading={loading && !data}
            >
                {isInitialLoading ? (
                    <Loading />
                ) : items.length === 0 ? (
                    <>
                        <EmptyScreen
                            image={NoTalentsImage}
                            text={i18n.t("screens.client_profile.tabs.employees.no_workers")}
                        />
                        <Button
                            size="large"
                            testID="button-new-invitation"
                            style={styles.startButton}
                            onPress={goToSendInvitation}
                            label={i18n.t("screens.client_profile.tabs.employees.invite_talent")}
                        />
                    </>
                ) : (
                    <ListGroup isItemsCheckable={false} items={items} alignment="stretch" />
                )}
            </LoadingBoundary>
        </ScreenCard>
    );
};

const useStyles = () => {
    const { gridSize } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                startButton: {
                    marginHorizontal: gridSize,
                    alignSelf: "stretch",
                },
            }),
        [gridSize],
    );
};

export default ClientWorkersScreen;
